import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouteLocationNormalized,
  type NavigationGuardNext,
} from 'vue-router';
import { usePermissions } from '@/composables/usePermissions';

/** Route meta interface for typed route meta fields. */
declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    requiresAdmin?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/components/views/DashboardView.vue'),
    meta: { title: 'Dashboard - Property Management' },
  },
  {
    path: '/dashboard',
    redirect: '/',
  },
  {
    path: '/tenants',
    name: 'tenants',
    component: () => import('@/components/views/TenantsView.vue'),
    meta: { title: 'Tenants - Property Management' },
  },
  {
    path: '/tenants/new',
    name: 'tenants-new',
    component: () => import('@/components/views/TenantsView.vue'),
    meta: { title: 'Tenants - Property Management' },
  },
  {
    path: '/tenants/:id',
    name: 'tenant-detail',
    component: () => import('@/components/views/TenantDetailView.vue'),
    meta: { title: 'Tenant Details - Property Management' },
  },
  {
    path: '/leases',
    name: 'leases',
    component: () => import('@/components/views/LeasesView.vue'),
    meta: { title: 'Leases - Property Management' },
  },
  {
    path: '/leases/:id/ledger',
    name: 'ledger',
    component: () => import('@/components/views/LedgerView.vue'),
    meta: { title: 'Rent Ledger - Property Management' },
  },
  {
    path: '/leases/:id',
    name: 'lease-detail',
    component: () => import('@/components/views/LeaseDetailView.vue'),
    meta: { title: 'Lease Details - Property Management' },
  },
  {
    path: '/properties',
    name: 'properties',
    component: () => import('@/components/views/PropertiesView.vue'),
    meta: { title: 'Properties - Property Management' },
  },
  {
    path: '/properties/:id',
    name: 'property-detail',
    component: () => import('@/components/views/PropertyDetailView.vue'),
    meta: { title: 'Property Details - Property Management' },
  },
  {
    path: '/units/:id',
    name: 'unit-detail',
    component: () => import('@/components/views/UnitDetailView.vue'),
    meta: { title: 'Unit Details - Property Management' },
  },
  {
    path: '/documents',
    name: 'documents',
    component: () => import('@/components/views/DocumentCenterView.vue'),
    meta: { title: 'Documents - Property Management' },
  },
  {
    path: '/actions',
    name: 'actions',
    component: () => import('@/components/views/ActionsRequiredView.vue'),
    meta: { title: 'Actions Required - Property Management' },
  },
  {
    path: '/tax-parcels',
    name: 'tax-parcels',
    component: () => import('@/components/views/TaxParcelsView.vue'),
    meta: { title: 'Tax Parcels - Property Management', requiresAdmin: true },
  },
  {
    path: '/reports/compliance',
    name: 'compliance-report',
    component: () => import('@/components/views/ComplianceReportView.vue'),
    meta: {
      title: 'Compliance Report - Property Management',
      requiresAdmin: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/components/views/NotFoundView.vue'),
    meta: { title: 'Not Found - Property Management' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

/** Navigation guard: admin-only route protection + page title. */
router.beforeEach(
  (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    // Admin-only route guard
    if (to.meta.requiresAdmin) {
      const { canSeeAdminControls } = usePermissions();
      if (!canSeeAdminControls()) {
        next({ name: 'dashboard' });
        return;
      }
    }

    // Set page title
    if (to.meta.title) {
      document.title = to.meta.title;
    }

    next();
  },
);

/**
 * Prefetch map: after navigating to a route, prefetch chunks for
 * likely-next routes so subsequent navigation feels instant.
 */
const prefetchMap: Record<string, Array<() => Promise<unknown>>> = {
  dashboard: [
    () => import('@/components/views/TenantsView.vue'),
    () => import('@/components/views/ActionsRequiredView.vue'),
  ],
  tenants: [
    () => import('@/components/views/TenantDetailView.vue'),
  ],
  'tenants-new': [
    () => import('@/components/views/TenantDetailView.vue'),
  ],
  'tenant-detail': [
    () => import('@/components/views/LeasesView.vue'),
    () => import('@/components/views/DocumentCenterView.vue'),
  ],
  leases: [
    () => import('@/components/views/LeaseDetailView.vue'),
    () => import('@/components/views/LedgerView.vue'),
  ],
  'lease-detail': [
    () => import('@/components/views/LedgerView.vue'),
  ],
  properties: [
    () => import('@/components/views/PropertyDetailView.vue'),
  ],
  'property-detail': [
    () => import('@/components/views/UnitDetailView.vue'),
  ],
  actions: [
    () => import('@/components/views/TenantDetailView.vue'),
  ],
  ledger: [
    () => import('@/components/views/LeaseDetailView.vue'),
    () => import('@/components/views/DocumentCenterView.vue'),
  ],
  documents: [
    () => import('@/components/views/TenantDetailView.vue'),
    () => import('@/components/views/LedgerView.vue'),
  ],
  'unit-detail': [
    () => import('@/components/views/PropertyDetailView.vue'),
  ],
};

/** Prefetch adjacent route chunks after navigation settles. */
router.afterEach((to) => {
  const routeName = typeof to.name === 'string' ? to.name : '';
  const chunks = prefetchMap[routeName];
  if (!chunks) return;

  const prefetch = () => {
    for (const load of chunks) {
      load().catch(() => {
        /* prefetch failure is non-critical */
      });
    }
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(prefetch);
  } else {
    setTimeout(prefetch, 100);
  }
});

export default router;

/**
 * Helper: get the "active nav section" name from a route.
 * Used by NavHeader to highlight the correct nav item.
 */
export function getNavSection(
  routeName: string | symbol | null | undefined,
): string {
  if (!routeName || typeof routeName === 'symbol') return '';
  const name = routeName as string;

  // Map detail views to their parent section
  if (name === 'tenant-detail') return 'tenants';
  if (name === 'property-detail') return 'properties';
  if (name === 'unit-detail') return 'properties';
  if (name === 'lease-detail' || name === 'ledger') return 'leases';
  if (name === 'tenants-new') return 'tenants';

  return name;
}
